import { useAuth0 } from '@auth0/auth0-react';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { ObjectStore } from './ObjectStore';
import { AccountsGateway } from './impl/AccountsGateway';
import { AnalyticsGateway } from './impl/AnalyticsGateway';
import { AutomationGateway } from './impl/AutomationGateway';
import { IntegrationGateway } from './impl/IntegrationGateway';
import { InventoryGateway } from './impl/InventoryGateway';
import { ProductsGateway } from './impl/ProductsGateway';
import { SalesGateway } from './impl/SalesGateway';
import { ShippingGateway } from './impl/ShippingGateway';
import { SimulationGateway } from './impl/SimulationGateway';
import { SupplyGateway } from './impl/SupplyGateway';
import { UserMembershipSorter } from './impl/UserMembershipSorter';
import { WarehousingGateway } from './impl/WarehousingGateway';

// potentially refactor gateways to use a non-specific interface
export type GatewayContextData = {
  accountsGateway: AccountsGateway;
  analyticsGateway: AnalyticsGateway;
  automationGateway: AutomationGateway;
  inventoryGateway: InventoryGateway;
  productsGateway: ProductsGateway;
  salesGateway: SalesGateway;
  shippingGateway: ShippingGateway;
  simulationGateway: SimulationGateway;
  supplyGateway: SupplyGateway;
  warehousingGateway: WarehousingGateway;
  integrationGateway: IntegrationGateway;
};

export const GatewayContext = createContext<GatewayContextData>({} as GatewayContextData);
const objectStore = new ObjectStore('accounts');
const userMembershipSorter = new UserMembershipSorter(objectStore);

export function useGateways() {
  return useContext(GatewayContext);
}

export function GatewayProvider({ children }: PropsWithChildren) {
  const { getAccessTokenSilently } = useAuth0();
  const contextData = useMemo(
    () => ({
      accountsGateway: new AccountsGateway(getAccessTokenSilently, userMembershipSorter),
      analyticsGateway: new AnalyticsGateway(getAccessTokenSilently),
      automationGateway: new AutomationGateway(getAccessTokenSilently),
      inventoryGateway: new InventoryGateway(getAccessTokenSilently),
      productsGateway: new ProductsGateway(getAccessTokenSilently),
      salesGateway: new SalesGateway(getAccessTokenSilently),
      shippingGateway: new ShippingGateway(getAccessTokenSilently),
      simulationGateway: new SimulationGateway(getAccessTokenSilently),
      supplyGateway: new SupplyGateway(getAccessTokenSilently),
      warehousingGateway: new WarehousingGateway(getAccessTokenSilently),
      integrationGateway: new IntegrationGateway(),
    }),
    [getAccessTokenSilently],
  );

  return <GatewayContext.Provider value={contextData}>{children}</GatewayContext.Provider>;
}
